import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Badge, Button, Card, PageHeader } from '@/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { SortableTable, type SortableColumn } from '@/components/ui/SortableTable';
import { fetchSeoOpportunities } from '@/data/sources-seod';
import { syncSeoSearchAnalytics } from '@/data/sources';
import type { SeoOpportunity, SeoOpportunityWindow } from '@/types/seod';
import { api } from '@/lib/api';
import { formatRelative } from '@/lib/utils';
import { useActiveDomain } from '@/hooks/useActiveDomain';
import { filterRowsByActiveDomain, scopeLabel } from '@/pages/seo/domain-scope';
import { stripInsightMarkdown } from '@/components/insightText';

/* /seo/opportunities — SEO Opportunities (W4a + SEO-WIRING 2026-07-20; GSC
 * date-range control 2026-08-01). A prioritised list, each row honestly
 * source-attributed:
 *   1. Ranked-organic keyword opportunities — REAL keywords the domain already
 *      ranks for (cached DataForSEO ranked_keywords dataset), evidence-scored
 *      by the shared SeoOpportunitySelector (the SAME source Aura + the Exec
 *      Dashboard use). Position/volume/difficulty/traffic pass through honestly
 *      (null = n/a). Each carries an exact action + a "Prepare draft" affordance.
 *   2. AI-grounded keyword_opportunity insights (stored, source "gsc").
 *   3. Deterministic striking-distance rows (newest stored position in 4-20).
 *   4. Demand — not ranking yet: Search Console queries with real impressions
 *      but a deep/absent position, read directly from bc_seo_search_analytics
 *      for the SELECTED window (impressions ≥ threshold, position > 20 or null).
 *
 * DATE-RANGE CONTROL (owner GO 2026-08-01) — a header dropdown picks the window
 * (28d/3m/12m/16m). Selecting a window ONLY re-reads the already-stored rows for
 * it (no surprise sync). A SEPARATE "Refresh this window" is the ONLY affordance
 * that spends a GSC pull; on success it re-queries. Default 28d — first load is
 * unchanged.
 *
 * HONESTY + SAFETY INVARIANTS:
 *  - The opportunities READ is FREE + read-only (cached-only; no provider call).
 *  - Changing the window NEVER auto-syncs; the empty state teaches Refresh.
 *  - Source NEVER blended without labelling — every row wears its own badge.
 *  - Null-honest: metrics render as em-dash when not carried; never fabricated.
 */

const ROW_LIMIT = 200;

const WINDOW_OPTIONS: ReadonlyArray<{ value: SeoOpportunityWindow; label: string }> = [
  { value: '28d', label: '28 days' },
  { value: '3m', label: '3 months' },
  { value: '12m', label: '12 months' },
  { value: '16m', label: 'All time (~16 months)' },
];

function windowLabel(w: SeoOpportunityWindow): string {
  return WINDOW_OPTIONS.find((o) => o.value === w)?.label ?? '28 days';
}

function typeBadge(o: SeoOpportunity) {
  if (o.type === 'ranked_organic') return <Badge tone="success">Ranked organic</Badge>;
  if (o.type === 'demand_not_ranking') return <Badge tone="warning">Demand — not ranking yet</Badge>;
  return o.type === 'ai_grounded' ? (
    <Badge tone="ai">Aura Recommends</Badge>
  ) : (
    <Badge tone="engine">Striking distance</Badge>
  );
}

/** A ranked-organic row shows its exact recommended action; a striking-distance
 *  row shows its position; a demand row shows its demand-scored opportunity
 *  string; an AI-grounded row shows its subject/body. Each renders honestly with
 *  no cross-contamination. */
function summaryCell(o: SeoOpportunity): string {
  if (o.type === 'ranked_organic') {
    return o.action || o.reasoning || '—';
  }
  if (o.type === 'demand_not_ranking') {
    return o.action || o.reasoning || stripInsightMarkdown(o.body || o.subject || '') || '—';
  }
  if (o.type === 'striking_distance') {
    return o.position === null
      ? '—'
      : `Ranks #${o.position} — page-one-adjacent, within striking distance`;
  }
  return stripInsightMarkdown(o.subject || o.body || '—') || '—';
}

/** "Prepare draft" affordance — POSTs the server-provided request to the
 *  EXISTING recommend accept flow (POST /v1/ai/recommend). No new route. */
function PrepareDraftButton({ o }: { o: SeoOpportunity }) {
  const [done, setDone] = useState(false);
  const pd = o.prepare_draft;
  const mut = useMutation({
    mutationFn: async () => {
      if (!pd) return null;
      return api.post(pd.request.endpoint.replace(/^\/v1/, ''), pd.request.payload);
    },
    onSuccess: () => setDone(true),
  });
  if (!pd || !pd.available) return <span className="text-text-tertiary">—</span>;
  return (
    <button
      type="button"
      disabled={mut.isPending || done}
      onClick={() => mut.mutate()}
      className="rounded-md border border-border-subtle px-2 py-1 text-xs text-ai hover:bg-surface-hover disabled:opacity-60"
    >
      {done ? 'Draft requested' : mut.isPending ? 'Preparing…' : pd.label || 'Prepare draft'}
    </button>
  );
}

const COLUMNS: SortableColumn<SeoOpportunity>[] = [
  {
    key: 'type',
    header: 'Type',
    sortable: true,
    sortValue: (o) => o.type,
    render: typeBadge,
    cellClassName: 'whitespace-nowrap',
  },
  {
    key: 'keyword',
    header: 'Keyword',
    sortable: true,
    sortValue: (o) => o.keyword,
    render: (o) => o.keyword || '—',
    truncate: true,
    maxWidthClass: 'max-w-[220px]',
    title: (o) => o.keyword ?? undefined,
    cellClassName: 'font-medium text-text-primary',
  },
  {
    key: 'position',
    header: 'Position',
    align: 'right',
    numeric: true,
    sortable: true,
    sortValue: (o) => o.position,
    render: (o) => (
      <span className="font-mono text-text-secondary">{o.position === null ? '—' : o.position}</span>
    ),
    cellClassName: 'whitespace-nowrap',
  },
  {
    key: 'impressions',
    header: 'Impressions',
    align: 'right',
    numeric: true,
    sortable: true,
    sortValue: (o) => o.impressions ?? null,
    render: (o) => (
      <span className="font-mono text-text-secondary">
        {o.impressions === null || o.impressions === undefined ? '—' : o.impressions.toLocaleString()}
      </span>
    ),
    cellClassName: 'whitespace-nowrap',
  },
  {
    key: 'volume',
    header: 'Volume',
    align: 'right',
    numeric: true,
    sortable: true,
    sortValue: (o) => o.volume ?? null,
    render: (o) => (
      <span className="font-mono text-text-secondary">
        {o.volume === null || o.volume === undefined ? '—' : o.volume.toLocaleString()}
      </span>
    ),
    cellClassName: 'whitespace-nowrap',
  },
  {
    key: 'difficulty',
    header: 'KD',
    align: 'right',
    numeric: true,
    sortable: true,
    sortValue: (o) => o.difficulty ?? null,
    render: (o) => (
      <span className="font-mono text-text-secondary">
        {o.difficulty === null || o.difficulty === undefined ? '—' : o.difficulty}
      </span>
    ),
    cellClassName: 'whitespace-nowrap',
  },
  {
    key: 'score',
    header: 'Score',
    align: 'right',
    numeric: true,
    sortable: true,
    sortValue: (o) => o.score ?? null,
    render: (o) =>
      o.score === null || o.score === undefined ? (
        <span className="font-mono text-text-tertiary">—</span>
      ) : (
        <Badge tone={o.score >= 70 ? 'success' : o.score >= 45 ? 'warning' : 'neutral'}>{o.score}</Badge>
      ),
    cellClassName: 'whitespace-nowrap',
  },
  {
    key: 'summary',
    header: 'Opportunity',
    render: (o) => <span className="text-text-secondary">{summaryCell(o)}</span>,
    truncate: true,
    maxWidthClass: 'max-w-[420px]',
    title: summaryCell,
  },
  {
    key: 'prepare',
    header: 'Draft',
    render: (o) => <PrepareDraftButton o={o} />,
    cellClassName: 'whitespace-nowrap',
  },
  {
    key: 'domain',
    header: 'Domain',
    render: (o) => o.domain || '—',
    truncate: true,
    maxWidthClass: 'max-w-[180px]',
    title: (o) => o.domain ?? undefined,
    cellClassName: 'text-text-tertiary',
  },
  {
    key: 'source',
    header: 'Source',
    render: (o) => <span className="font-mono text-xs text-text-tertiary">{o.source}</span>,
    cellClassName: 'whitespace-nowrap',
  },
];

export function SeoOpportunitiesPage() {
  // Scope the fetch to the SELECTED 'Viewing' domain server-side so the panel
  // never pulls another domain's rows (owner 2026-07-21); the client-side
  // filter below stays as defence-in-depth.
  const { active } = useActiveDomain();
  const projectId = active?.projectId ?? null;

  // Date-range window (owner GO 2026-08-01). Default 28d — first load unchanged.
  const [selectedWindow, setSelectedWindow] = useState<SeoOpportunityWindow>('28d');

  // FREE read-only surface — safe to auto-load on mount. Re-queries per window;
  // selecting a window NEVER triggers a sync (window key drives the refetch).
  const oppsQ = useQuery({
    queryKey: ['seo', 'opportunities', ROW_LIMIT, projectId, selectedWindow],
    queryFn: () => fetchSeoOpportunities(ROW_LIMIT, projectId, selectedWindow),
  });

  // The ONLY affordance that spends a GSC pull. Explicit, per-window, never
  // automatic. On success it re-queries the (now-populated) window.
  const refreshMut = useMutation({
    mutationFn: () => {
      if (projectId == null) throw new Error('no_project');
      return syncSeoSearchAnalytics(String(projectId), selectedWindow);
    },
    onSuccess: () => {
      void oppsQ.refetch();
    },
  });

  const data = oppsQ.data ?? null;
  const opportunities = filterRowsByActiveDomain(data?.opportunities ?? [], active);
  // "Aura Recommends" insights: aggregate GSC records with NO exact keyword --
  // shown in their OWN section, never as a blank keyword-opportunity table row.
  const insights = filterRowsByActiveDomain(data?.insights ?? [], active);

  const rangeLabel = windowLabel(selectedWindow);

  const headerActions = (
    <>
      <select
        aria-label="Opportunity date range"
        value={selectedWindow}
        onChange={(e) => setSelectedWindow(e.target.value as SeoOpportunityWindow)}
        className="h-control rounded-md border border-border-default bg-canvas px-2 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30"
      >
        {WINDOW_OPTIONS.map((w) => (
          <option key={w.value} value={w.value}>
            {w.label}
          </option>
        ))}
      </select>
      <Button
        variant="secondary"
        disabled={projectId == null || refreshMut.isPending}
        onClick={() => refreshMut.mutate()}
        title={
          projectId == null
            ? 'Choose a domain to refresh this window'
            : 'Pull fresh Search Console data for the selected range'
        }
      >
        {refreshMut.isPending ? 'Refreshing…' : 'Refresh this window'}
      </Button>
    </>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="SEO Opportunities"
        subtitle="Prioritised, evidence-backed opportunities — real ranked-organic keywords you already rank for (scored for realistic page-one wins), alongside Aura Recommends insights, deterministic “striking distance” keywords and Search Console demand you don’t rank for yet. Free to view, source-attributed per row, and never a proprietary-index claim."
        actions={headerActions}
      />

      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="neutral">{scopeLabel(active)}</Badge>
          <Badge tone="neutral">Range: {rangeLabel}</Badge>
          {data && (
            <>
              <Badge tone="success">{data.ranked_organic_count} ranked organic</Badge>
              <Badge tone="ai">{data.ai_grounded_count} Aura Recommends</Badge>
              <Badge tone="engine">{data.striking_distance_count} striking distance</Badge>
              <Badge tone="warning">{data.demand_count} demand</Badge>
            </>
          )}
          <span className="ml-auto text-xs text-text-tertiary">
            {data?.fetched_at
              ? `Updated ${formatRelative(data.fetched_at)}`
              : 'Read-only from the cached ranked-organic dataset, stored insights and rankings'}
          </span>
        </div>
        <p className="mt-1 text-sm text-text-secondary">
          Ranked-organic rows are real keywords your site already ranks for (cached DataForSEO data),
          scored for realistic page-one wins with an exact recommended action and a one-click “Prepare
          draft”. Striking-distance keywords rank in positions 4–20 on their newest stored observation.
          Aura Recommends rows carry the exact Search Console query verbatim from stored keyword-opportunity insights.
          Demand rows are Search Console queries with real impressions that you don’t rank for yet, for the selected
          range. Changing the range re-reads stored data only — use “Refresh this window” to pull new Search Console
          data. None is a fresh, paid provider call.
        </p>

        {refreshMut.isError && (
          <p className="mt-2 text-xs text-danger">
            The {rangeLabel} refresh could not be completed — nothing was fetched. Try again.
          </p>
        )}
        {refreshMut.isSuccess && refreshMut.data && (
          <p className="mt-2 text-xs text-text-tertiary">
            {refreshMut.data.no_data
              ? `Refreshed ${rangeLabel} — Google Search Console returned no query rows for this range. Nothing was invented.`
              : `Refreshed ${rangeLabel} — ${refreshMut.data.synced_rows} ${
                  refreshMut.data.synced_rows === 1 ? 'query' : 'queries'
                } pulled from Google Search Console.`}
          </p>
        )}

        <div className="mt-4">
          {oppsQ.isPending ? (
            <LoadingState rows={4} />
          ) : oppsQ.error ? (
            <ErrorState error={oppsQ.error} onRetry={() => void oppsQ.refetch()} />
          ) : opportunities.length === 0 ? (
            <EmptyState
              title={`No opportunities for ${rangeLabel} yet`}
              teach={`Nothing is stored for this range. Use “Refresh this window” to pull ${rangeLabel} from Google Search Console — ranked-organic, striking-distance and demand rows appear once data exists. Nothing is fetched automatically when you change the range.`}
            />
          ) : (
            <div className="overflow-hidden rounded-lg border border-border-subtle bg-surface">
              <SortableTable
                rows={opportunities}
                columns={COLUMNS}
                getRowKey={(o, i) => `${o.type}-${o.keyword ?? 'na'}-${i}`}
                minWidthClass="min-w-[1160px]"
                caption="Prioritised SEO opportunities, source-attributed per row"
              />
              <div className="border-t border-border-subtle px-3 py-2 text-[11px] text-text-tertiary">
                {opportunities.length}{' '}
                {opportunities.length === 1 ? 'opportunity' : 'opportunities'} · {rangeLabel} · each row
                wears its own source; ranked-organic + striking-distance rows are never labelled as AI
              </div>
            </div>
          )}
        </div>
      </Card>

      {insights.length > 0 && (
        <Card className="p-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-text-primary">Aura Recommends</span>
            <Badge tone="ai">
              {insights.length} insight{insights.length === 1 ? '' : 's'}
            </Badge>
            <span className="ml-auto text-xs text-text-tertiary">
              Search Console-grounded insights with no single target keyword — shown separately from keyword
              opportunities, never as a blank keyword row
            </span>
          </div>
          <ul className="mt-3 space-y-2">
            {insights.map((n, i) => (
              <li
                key={`aura-insight-${i}`}
                className="rounded-lg border border-border-subtle bg-surface p-3"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-text-primary">
                    {stripInsightMarkdown(n.subject || '') || 'SEO insight'}
                  </span>
                  {n.domain && <Badge tone="neutral">{n.domain}</Badge>}
                  <span className="ml-auto font-mono text-[11px] text-text-tertiary">{n.source}</span>
                </div>
                {n.body && (
                  <p className="mt-1 text-sm text-text-secondary">{stripInsightMarkdown(n.body)}</p>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
